/**
 * @fileoverview useSecondsUntil — the ONE ticking countdown to a deadline
 * @package @puffin-core/countdown
 * @module useSecondsUntil
 * @description Whole seconds until `deadlineMs`, re-read on every whole-second boundary until it reaches zero
 *   (POS-STATION-IDLE-CLOCK-001 U4; moved here from `@puffin-core/page-shell`, which re-exports it, so that a package
 *   page-shell cannot reach (palm-native) counts down the same way).
 *
 *   - Each read is `countdownClock.remainingMsOf`: the smaller of the wall clock's and a monotonic clock's
 *     remainders, so neither sleep nor a clock moved back makes it say more time is left than is.
 *   - The seconds round UP: it reads 1 until the deadline has passed and 0 only once it has, so a control that
 *     enables at zero never enables early.
 *   - A deadline that is not a finite number of epoch milliseconds (null, an unreadable date's `NaN`, a string, a
 *     `Date`) answers `null` and runs no timer: never `NaN` on a screen. The caller converts its deadline to epoch
 *     ms (temporal-core `instantEpochMs` for a server instant, which reads a TIMESTAMPTZ's wire form a strict
 *     engine's `Date.parse` cannot; `decodeJwtExpiryMs` for a token).
 *   - One timer per mounted hook, scheduled for the next boundary; a new deadline replaces it; at zero it stops.
 *
 *   This is a display. It decides nothing that a server or the ONE idle clock decides.
 *
 * @example
 *   const secondsLeft = useSecondsUntil(expiresAtMs); // null when there is no deadline
 */

import { useEffect, useState } from 'react';

import { readClocks, remainingMsOf } from './countdownClock.js';
import { useClockAnchor } from './useClockAnchor.js';

const SECOND_MS = 1000;

/**
 * @param {unknown} deadlineMs - The deadline, epoch milliseconds.
 * @returns {?number} Whole seconds left, rounded up (0 once it has passed), or null when there is no readable deadline.
 */
export function useSecondsUntil(deadlineMs) {
  const readable = Number.isFinite(deadlineMs);
  const deadline = readable ? /** @type {number} */ (deadlineMs) : null;
  const anchor = useClockAnchor(deadline);
  // A tick only asks for a render; the render reads the clocks itself, so every render shows the remainder as of
  // that render.
  const [, setTick] = useState(0);

  useEffect(() => {
    if (deadline === null) return undefined;
    let timer;
    const scheduleNextBoundary = () => {
      const left = remainingMsOf(deadline, anchor, readClocks());
      if (left <= 0) return;
      timer = setTimeout(
        () => {
          setTick((tick) => tick + 1);
          scheduleNextBoundary();
        },
        left % SECOND_MS || SECOND_MS,
      );
    };
    scheduleNextBoundary();
    return () => clearTimeout(timer);
  }, [deadline, anchor]);

  return deadline === null
    ? null
    : Math.ceil(remainingMsOf(deadline, anchor, readClocks()) / SECOND_MS);
}
